// Copyright (c) 2021-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import * as React from "react";
import { GET_BANNER_CLICK_URL } from "../../constants/backend";
import { BANNER_CLASS_NAME } from "../../constants/classNames";
import { BannerDTO } from "../../io/hyperify/core/banner/types/BannerDTO";
import { BannerType } from "../../io/hyperify/core/banner/types/BannerType";
import { LogService } from "../../io/hyperify/core/LogService";
import { ObserverDestructor } from "../../io/hyperify/core/Observer";
import { WindowLocationService } from "../../io/hyperify/frontend/services/WindowLocationService";
import { BannerService } from "../../services/BannerService";
import "./Banner.scss";

const LOG = LogService.createLogger('Banner');

export interface BannerProps {
    className?: string;
}

export interface BannerState {
    readonly banner: BannerDTO | undefined;
}

export class Banner extends React.Component<BannerProps, BannerState> {

    static defaultProps: Partial<BannerProps> = {};

    private _bannerUpdatedListener : ObserverDestructor | undefined;

    constructor(props: BannerProps) {
        super(props);
        this.state = {
            banner: undefined
        };
    }

    componentDidMount() {
        this._updateBanner();
        LOG.debug('componentDidMount: Listening BANNER_UPDATED');
        this._bannerUpdatedListener = BannerService.on(BannerService.Event.BANNER_UPDATED, () => {
            LOG.debug('componentDidMount: BANNER_UPDATED: Got event');
            this._updateBanner();
        });
        BannerService.increaseBannerVisibility();
    }

    componentWillUnmount() {
        if (this._bannerUpdatedListener !== undefined) {
            this._bannerUpdatedListener();
            this._bannerUpdatedListener = undefined;
        }
        BannerService.decreaseBannerVisibility();
    }

    render() {

        const styles : {[key: string] : any} = {};

        const banner : BannerDTO | undefined = this.state.banner;

        if (banner) {
            styles.backgroundImage = `url(${ banner.imageUrl })`;
            styles.backgroundSize = banner.type === BannerType.COVER ? 'cover' : 'contain';
        }

        const referrer = WindowLocationService.getReferrer();

        return (
            <div className={BANNER_CLASS_NAME + ' ' + (this.props.className ?? '')}
                 style={styles}
            >{banner
                ? (
                    <a className={BANNER_CLASS_NAME+'-l'}
                       target={'_blank'}
                       rel="noopener noreferrer nofollow"
                       href={GET_BANNER_CLICK_URL(banner, referrer)}
                       title={banner.title}
                    >{ banner.addTitleText || banner.addAltText ? (
                            <div className={BANNER_CLASS_NAME+'-c'}>
                                { banner.addTitleText ? <div className={BANNER_CLASS_NAME+'-c-t'}>{banner.title}</div> : null }
                                { banner.addAltText ? <div className={BANNER_CLASS_NAME+'-c-a'}>{banner.imageAlt}</div> : null }
                            </div>
                        ) : ' '}</a>
                ) : null}</div>
        );

    }

    private _updateBanner () {

        if (BannerService.isLoading()) {
            LOG.debug('_updateBanner: BannerService is loading')
            return;
        }

        const banner = BannerService.getBanner();
        if (banner) {
            LOG.debug('_updateBanner: Got banner: ', banner);
            this.setState({
                ...this.state,
                banner: banner
            });
        } else {
            LOG.debug('_updateBanner: No banner ');
        }

    }

}
